import { onlineManager, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'
import { toast } from 'sonner'
import type { UpdateTransportationFormFieldSchema } from '@/schemas/booking'
import type { SubmitHandler } from 'react-hook-form'
import { getFleetsQueryOptions } from '@/queries/query'
import { queryKeys } from '@/queries/keys'
import useBookingMutation from '@/hooks/useBookingMutation'
import ChangeBusModal from '@/pages/make-booking/bus-details/ModalChangeBus'
import { handleToastError } from '@/utils/error'

export const Route = createFileRoute(
  '/make-booking/$id/bus-details/$transportationId/change-bus',
)({
  loader: ({ context }) => {
    if (onlineManager.isOnline() && !context.isRestoring) {
      return context.queryClient.ensureInfiniteQueryData(
        getFleetsQueryOptions(),
      )
    }
    return context.queryClient.getQueryData(queryKeys.fleet.list())
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const router = useRouter()
  const bookingId = Route.useParams({
    select: (s) => s.id,
  })
  const transportationId = Route.useParams({
    select: (s) => s.transportationId,
  })

  const { updateTransportation } = useBookingMutation()
  const { data: resFleets } = useSuspenseInfiniteQuery(getFleetsQueryOptions())

  const fleets = resFleets.pages.flatMap((p) => p.data.transportation_types)

  const onClose = useCallback(() => {
    return router.history.back({ ignoreBlocker: true })
  }, [])

  const onSubmit = useCallback<
    SubmitHandler<UpdateTransportationFormFieldSchema>
  >(
    (data, e) => {
      e?.stopPropagation()
      e?.preventDefault()
      return updateTransportation.mutateAsync(data, {
        onSuccess: () => {
          toast.success('Successfully change bus')
          if (router.history.canGoBack()) return router.history.back()
          return navigate({
            to: '/make-booking/$id/bus-details/$transportationId/trip-type',
            params: {
              id: bookingId,
              transportationId,
            },
          })
        },
        onError: handleToastError,
      })
    },
    [
      router.history,
      navigate,
      bookingId,
      transportationId,
      updateTransportation,
    ],
  )

  return (
    <ChangeBusModal
      isOpen={true}
      fleets={fleets}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}
